import i18n from 'i18next';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import _en from '../locales/en.json';
import _it from '../locales/it.json';
import _es from '../locales/es.json';
import _de from '../locales/de.json';

// Kwick's
import kwick_en from '../locales/kwick/en.json';
import kwick_it from '../locales/kwick/it.json';
import kwick_de from '../locales/kwick/de.json';
import plenitude_en from '../locales/plenitude/en.json';
import plenitude_it from '../locales/plenitude/it.json';
import solectivo_en from '../locales/solectivo/en.json';
import solectivo_es from '../locales/solectivo/es.json';
import eon_it from '../locales/eon/it.json';
import eon_en from '../locales/eon/en.json';

import merge from 'lodash/merge';

const ENV = (import.meta as any).env;

const resources: Record<string, any> = {
  _en: { common: _en },
  _it: { common: _it },
  _es: { common: _es },
  _de: { common: _de },
  kwick_en: { common: merge({}, _en, kwick_en) },
  kwick_it: { common: merge({}, _it, kwick_it) },
  kwick_de: { common: merge({}, _de, kwick_de) },
  plenitude_en: { common: merge({}, _en, plenitude_en) },
  plenitude_it: { common: merge({}, _it, plenitude_it) },
  solectivo_en: { common: merge({}, _en, solectivo_en) },
  solectivo_es: { common: merge({}, _es, solectivo_es) },
  eon_en: { common: merge({}, _en, eon_en) },
  eon_it: { common: merge({}, _it, eon_it) },
  admin_en: { common: merge({}, _en) },
  admin_it: { common: merge({}, _it) },
  admin_es: { common: merge({}, _es) },
  admin_de: { common: merge({}, _de) },
  hive_en: { common: merge({}, _en) },
  hive_it: { common: merge({}, _it) },
  hive_es: { common: merge({}, _es) },
};

/**
 * Resolve the initial language for i18n.
 *
 * Priority:
 * 1. User's saved preference in localStorage (manual selection via LanguageSelector)
 * 2. Device/browser locale (mobile apps only, when no saved preference exists)
 * 3. VITE_APP_LANGUAGE env var fallback (typically "it")
 *
 * For device locale, only the base language code is matched (e.g. "de-AT" → "de").
 * If the device language is not among the app's available resources, falls back to
 * VITE_APP_LANGUAGE (Italian for kwick/plenitude).
 */
function getInitialLanguage(): string {
  const org = ENV.VITE_APP_ORG;
  const fallbackLang = ENV.VITE_APP_LANGUAGE || 'it';
  const langKey = `${org}:lang`;

  // 1. Check saved user preference
  try {
    const saved = localStorage.getItem(langKey);
    if (saved && resources[saved]) {
      return saved;
    }
  } catch { /* localStorage unavailable */ }

  // 2. For mobile apps, detect device locale
  if (ENV.VITE_APP_IS_WEB !== 'true') {
    try {
      const deviceLang = (navigator.language || '').split('-')[0].toLowerCase();
      if (deviceLang && resources[`${org}_${deviceLang}`]) {
        const resolved = `${org}_${deviceLang}`;
        // Persist so it's not re-detected every time
        try { localStorage.setItem(langKey, resolved); } catch { /* ignore */ }
        return resolved;
      }
    } catch { /* navigator unavailable */ }
  }

  // 3. Env var fallback
  return `${org}_${fallbackLang}`;
}

i18n
  .use(initReactI18next)
  .init({
  compatibilityJSON: 'v3',
  ns: ['common'],
  defaultNS: 'common',
  resources,
  // debug: true,
  lng: getInitialLanguage(),
  fallbackLng: `${ENV.VITE_APP_ORG}_${ENV.VITE_APP_LANGUAGE || 'it'}`,
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense:false,
 }
});


export const TranslationsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
